/* eslint-disable @eslint-react/dom/no-dangerously-set-innerhtml */
/* eslint-disable react/no-danger, react/jsx-props-no-spreading */
import { useForm } from '@folklore/forms';
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { isString } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAuthLogin, useAuthSendToken } from '../../hooks/useAuth';

import { useLabels } from '../../contexts/LabelsContext';
import { useThemeClassName } from '../../contexts/ThemeContext';
import Button from '../buttons/Button';
import StyledButton from '../buttons/StyledButton';
import SubmitButton from '../buttons/SubmitButton';
import TextField from '../fields/Text';

import styles from '../../../styles/forms/token-form.module.scss';

interface TokenFormProps {
    value?: { phone?: string };
    charsCount?: number;
    autoSubmit?: boolean;
    withCancelButton?: boolean;
    title?: React.ReactNode | false;
    description?: React.ReactNode | false;
    note?: React.ReactNode | false;
    resendLabel?: React.ReactNode | false;
    submitLabel?: React.ReactNode | false;
    className?: string;
    onSuccess?: (user: Participant) => void;
    onClickClose?: () => void;
}

function TokenForm({
    value = null,
    charsCount = 4,
    autoSubmit = true,
    withCancelButton = true,
    title: providedTitle = null,
    description: providedDescription = null,
    note = null,
    resendLabel: providedResendLabel = null,
    submitLabel: providedSubmitLabel = null,
    className = null,
    onSuccess = null,
    onClickClose = null,
}: TokenFormProps) {
    const placeholder = useMemo(() => '_'.repeat(charsCount), [charsCount]);
    const { phone } = value || {};
    const route = useUrlGenerator();
    const action = route('auth.login');
    const themeClassName = useThemeClassName(styles);
    const inputRef = useRef<HTMLInputElement>(null);
    const [resending, setResending] = useState(false);
    const {
        tokenModalTitle = null,
        tokenModalDescription = null,
        tokenModalResend = null,
        tokenModalSubmit = null,
    } = useLabels();

    const title = providedTitle ?? tokenModalTitle;
    const description = providedDescription ?? tokenModalDescription;
    const resendLabel = providedResendLabel ?? tokenModalResend;
    const submitLabel = providedSubmitLabel ?? tokenModalSubmit;

    const onEnterComplete = useCallback(
        (user) => {
            if (onSuccess !== null) {
                onSuccess(user);
            }
        },
        [onSuccess],
    );

    const { sendTokenAsync } = useAuthSendToken();
    const { loginAsync } = useAuthLogin();

    const postForm = useCallback((postAction, postData) => loginAsync(postData), [loginAsync]);

    const {
        value: formValue,
        setValue: setFormValue,
        fields,
        onSubmit,
        errors,
        loading,
        generalError = null,
        status: formStatus,
        submit,
    } = useForm({
        value,
        // initialValue: value,
        fields: ['token', 'phone'],
        action,
        postForm,
        onComplete: onEnterComplete,
    });

    const { token: tokenField } = fields || {};
    const { value: tokenValue } = tokenField || {};
    const { token: tokenErrors = null } = errors || {};

    const resendLink = useCallback(() => {
        setResending(true);
        sendTokenAsync(value).then(({ token_sent: tokenSent = false }) => {
            setResending(false);
            if (tokenSent) {
                setFormValue({
                    ...formValue,
                    token: '',
                });
                inputRef.current.focus();
            }
        });
    }, [phone, formValue, setFormValue, sendTokenAsync]);

    const onCancelClick = useCallback(() => {
        if (onClickClose !== null) {
            onClickClose();
        }
    }, [onClickClose]);

    // autosubmit after last character entered
    const submittedRef = useRef(false);
    useEffect(() => {
        if (autoSubmit && (tokenValue || '').length === charsCount && !submittedRef.current) {
            submittedRef.current = true;
            submit();
        }
    }, [autoSubmit, tokenValue, charsCount]);

    // onError -> focus and select text
    useEffect(() => {
        if (formStatus === 'error') {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [formStatus]);

    return (
        <form
            className={classNames([
                styles.container,
                'form-token',
                {
                    [className]: className !== null,
                    [styles.loading]: loading || resending,
                    [styles.withCancelButton]: withCancelButton,
                },
                themeClassName,
                className,
            ])}
            onSubmit={(e) => {
                submittedRef.current = true;
                if (!submittedRef.current) {
                    onSubmit(e);
                } else {
                    e.preventDefault();
                }
            }}
            action={action}
        >
            <div className={styles.inner}>
                {title !== false ? (
                    <h1
                        className={styles.title}
                        dangerouslySetInnerHTML={
                            isString(title)
                                ? {
                                      __html: title,
                                  }
                                : undefined
                        }
                    >
                        {title === null ? (
                            <FormattedMessage
                                defaultMessage="Are you ready?"
                                description="Login token modal title"
                            />
                        ) : !isString(title) ? (
                            title
                        ) : undefined}
                    </h1>
                ) : null}
                {description !== false ? (
                    <p
                        className={styles.description}
                        dangerouslySetInnerHTML={
                            description !== null
                                ? {
                                      __html: description,
                                  }
                                : undefined
                        }
                    >
                        {description === null ? (
                            <FormattedMessage
                                defaultMessage="We’ll send an activation code to your phone shortly. Once received, please enter the code here to proceed."
                                description="Login token modal description"
                            />
                        ) : undefined}
                    </p>
                ) : null}
                <div className={styles.fields}>
                    <TextField
                        className={styles.tokenInput}
                        {...fields.token}
                        placeholder={placeholder}
                        inputMode="numeric"
                        maxLength={charsCount}
                        ref={inputRef}
                        autoFocus
                        // required
                    />
                    {tokenErrors !== null || generalError !== null ? (
                        <div className={styles.errors}>
                            {(tokenErrors || []).map((error, errorIndex) => (
                                <div key={`error-${errorIndex + 1}`}>{error}</div>
                            ))}
                            {generalError !== null ? <div>{generalError}</div> : null}
                        </div>
                    ) : null}
                </div>
                <Button className={styles.resendLink} onClick={resendLink}>
                    {resendLabel || (
                        <FormattedMessage defaultMessage="Resend code" description="Button label" />
                    )}
                </Button>
                {note !== null ? <p className={styles.note}>{note}</p> : null}
                <div className={styles.actions}>
                    {withCancelButton ? (
                        <StyledButton bordered className={styles.cancel} onClick={onCancelClick}>
                            <FormattedMessage defaultMessage="Cancel" description="Button label" />
                        </StyledButton>
                    ) : null}
                    <SubmitButton colored={false} className={styles.submit} type="submit">
                        {submitLabel !== null ? (
                            <p>{submitLabel}</p>
                        ) : (
                            <FormattedMessage defaultMessage="Confirm" description="Button label" />
                        )}
                    </SubmitButton>
                </div>
            </div>
        </form>
    );
}

export default TokenForm;
